// Forked from ../../ResultsDropdown/ResultsDropdown.tsx (its result groups) — keep in sync until
// phase_0 cleanup.
import React from 'react';
import styled from 'styled-components';
import { useTranslate } from '@bynder/localization';
import { List, token } from '@bynder/design-system';
import { NavigableHeadingButton } from '../NavigableHeadingButton';
import { navigateTo } from '../../../../helpers/windowLocation';
import { ResultsCategory } from '../constants';
import { GroupAssets } from './GroupAssets';
import { GroupCollections } from './GroupCollections';
import { GroupGuidelines } from './GroupGuidelines';
import { GroupTags } from './GroupTags';
import { GroupMetaproperties } from './GroupMetaproperties';
import { NoResults } from './NoResults';
import { StyledDivider } from '../SharedComponents';
import { ScrollFade } from '../ScrollFade';
import { hasResultsForCategory } from './utils';
import { useSearch, useSearchScope } from '../../SpotlightState';

// Renders the selected category's result section: heading (with an optional
// "View All (count)") and the row list underneath. Assets also carries the
// Tags/Metaproperties rows beneath it, per design.
export function ResultsSection() {
	const { selectedResultsCategory } = useSearchScope();
	const { searchTerm, searchResults, defaultSearchAction } = useSearch();
	const { translate } = useTranslate();

	if (selectedResultsCategory && !hasResultsForCategory(selectedResultsCategory, searchResults)) {
		return <NoResults />;
	}

	if (selectedResultsCategory === ResultsCategory.Assets) {
		const hasTagsOrMetaproperties =
			searchResults.tags.length > 0 || searchResults.metaproperties.length > 0;

		return (
			<StyledAssetsCategoryWrapper>
				<StyledAssetsScrollArea $isCapped={hasTagsOrMetaproperties}>
					<NavigableHeadingButton onSelect={() => defaultSearchAction()}>
						{translate('spotlight.dropdown.assets.heading')}
						<StyledViewAll>
							<span>{translate('spotlight.dropdown.viewAll')}</span>
							<span> ({searchResults.assets.total})</span>
						</StyledViewAll>
					</NavigableHeadingButton>

					<List isContained>
						<ul>
							<GroupAssets assetResults={searchResults.assets} />
						</ul>
					</List>

					<ScrollFade />
				</StyledAssetsScrollArea>

				{hasTagsOrMetaproperties && <StyledSectionDivider />}

				{hasTagsOrMetaproperties && (
					<StyledTagsAndMetapropertiesWrapper>
						{searchResults.tags.length > 0 && <GroupTags tags={searchResults.tags} />}

						{searchResults.metaproperties.map((metaproperty) => (
							<GroupMetaproperties
								metaproperty={metaproperty}
								key={metaproperty.name}
							/>
						))}
					</StyledTagsAndMetapropertiesWrapper>
				)}
			</StyledAssetsCategoryWrapper>
		);
	}

	if (selectedResultsCategory === ResultsCategory.Collections) {
		const goToCollectionsSearchPage = () =>
			navigateTo(`/collections/?box=all&searchTerm=${encodeURIComponent(searchTerm)}`);

		return (
			<>
				<NavigableHeadingButton onSelect={goToCollectionsSearchPage}>
					{translate('spotlight.dropdown.collections.heading')}
					<StyledViewAll>
						<span>{translate('spotlight.dropdown.viewAll')}</span>
						<span> ({searchResults.collections.total})</span>
					</StyledViewAll>
				</NavigableHeadingButton>

				<List isContained>
					<ul>
						<GroupCollections collectionResults={searchResults.collections} />
					</ul>
				</List>

				<ScrollFade />
			</>
		);
	}

	if (selectedResultsCategory === ResultsCategory.Guidelines) {
		return (
			<>
				<StyledPlainHeading>
					{translate('spotlight.dropdown.guidelines.only')}
				</StyledPlainHeading>

				<List isContained>
					<ul>
						<GroupGuidelines guidelines={searchResults.guidelines.items} />
					</ul>
				</List>

				<ScrollFade />
			</>
		);
	}

	return null;
}

// Guidelines has no "View All", so its heading is a plain
// label rather than a NavigableHeadingButton.
const StyledPlainHeading = styled.div`
	display: flex;
	align-items: center;
	height: 32px;
	padding: 0 ${token.spacing5};
	font-weight: 500;
`;

// Caps and scrolls the asset list on its own, so Tags/Metaproperties below stay visible in the dropdown.
const StyledAssetsScrollArea = styled.div<{ $isCapped: boolean }>`
	${({ $isCapped }) =>
		$isCapped &&
		`
			position: relative;
			flex: 1 1 auto;
			min-height: 0;
			overflow-y: auto;
		`}
`;

const StyledAssetsCategoryWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;

const StyledTagsAndMetapropertiesWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${token.spacing4};
	flex: 0 0 auto;
`;

const StyledSectionDivider = styled(StyledDivider)`
	width: calc(100% + 2 * ${token.spacing3});
	margin: 20px calc(-1 * ${token.spacing3}) ${token.spacing3};
`;

const StyledViewAll = styled.div`
	color: ${token.colorTextDisabled};
	font-weight: 400 !important;

	> span:first-child {
		color: ${token.colorTextMuted};
	}
`;
